import { observable } from 'mobx';
import store from 'client/store';
import { getModelWhere } from 'helpers';
import { endOfDay, endOfMinute } from 'date-fns';

const PER_PAGE_SMALL = 6;
const PER_PAGE_SMALL_WITHOUT_BANNER = 8;

// Максимальное количество записей требуемых для каждого блока приоритетов
const PRIORITY_LIMITS = {
	1: 8,
	2: 3,
	3: 1,
};

export default class NewsStore {
	@observable filter = {
		county: null,
		topic: null,
		appurtenance: null,
	};

	@observable records = [];
	@observable eventsByDate = null;
	@observable isLoadingMain = true;
	@observable isLoadingSmall = true;
	@observable smallTab = 'last';
	@observable recordsSmall = [];
	@observable banner = null;

	constructor() {
		this.init();
	}

	init = async () => {
		await this.fetchAdditionalBanner();
		await this.fetchMain();
		await this.fetchSmall();
	};

	fetchAdditionalBanner = async () => {
		const [banner] = await store.model.AdditionalBanner.find({
			where: { siteId: store.ui.site && store.ui.site.id, organizationId: store.ui.organization && store.ui.organization.id },
			order: 'createdAt desc',
			limit: 1,
		});
		this.banner = banner;
	};

	fetchMain = async () => {
		this.isLoadingMain = true;
		const where = getModelWhere(store.model.ViewArticle, false);
		where.and.push({ or: [{ mainVisibleDateBefore: { gte: endOfDay(new Date()) } }, { mainVisibleDateBefore: { eq: null } }] });
		where.and.push({ publishAt: { lt: endOfMinute(new Date()) } });
		// В запросе по вкладкам тоже поправить showOnMain, если будет затрагиваться
		where.and.push({ showOnMain: true });
		this.filter.county && where.and.push({ countyId: this.filter.county.id });
		this.filter.topic && where.and.push({ topicId: this.filter.topic.id });
		this.filter.appurtenance && where.and.push({ appurtenanceId: this.filter.appurtenance.id });

		const [lowRecords, mediumRecords, highRecords] = await Promise.all(
			Object.keys(PRIORITY_LIMITS).map((key) => {
				const _where = JSON.parse(JSON.stringify(where));
				_where.and.push({ priority: key });
				return store.model.ViewArticle.find({
					where: _where,
					limit: PRIORITY_LIMITS[key],
					include: ['labels', 'magazine', 'county', 'topics', 'category', 'appurtenance'],
					order: 'publishAt desc',
				});
			})
		);

		// Если записей более высокого приоритета нет, то записи более низкого занимают их место.
		const sliceMedium = PRIORITY_LIMITS[2] - highRecords.length;
		const sliceMediumRecords = mediumRecords.slice(0, sliceMedium);
		const sliceLow = PRIORITY_LIMITS[1] - sliceMediumRecords.length;
		this.records = [...highRecords, ...sliceMediumRecords, ...lowRecords.slice(0, sliceLow)];

		this.isLoadingMain = false;
	};

	fetchSmall = async () => {
		this.isLoadingSmall = true;
		const where = getModelWhere(store.model.ViewArticle, false);
		where.and.push({ publishAt: { lt: endOfMinute(new Date()) } });
		this.filter.county && where.and.push({ countyId: this.filter.county.id });

		const filter = {
			where,
			include: ['labels', 'magazine', 'county', 'topics', 'category', 'appurtenance'],
			limit: this.banner ? PER_PAGE_SMALL : PER_PAGE_SMALL_WITHOUT_BANNER,
			order: ['priority desc', 'publishAt desc'],
		};
		this.recordsSmall = await store.model.ViewArticle.find(filter);

		this.isLoadingSmall = false;
	};

	setTab = async (tab) => {
		this.tab = tab;
		if (tab === 'all') {
			this.filter.appurtenance = null;
		}
		await this.fetchMain();
	};

	setTabSmallArticle = async (smallTab) => {
		this.smallTab = smallTab;
		await this.fetchSmall();
	};

	onChangeFilter = (field, record) => {
		this.filter[field] = record;
		this.fetchMain();
		if (field === 'county') this.fetchSmall();
	};
}
